import { Router } from '@angular/router';
import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  SimpleChanges,
  OnInit,
  OnChanges,
  AfterViewInit,
  HostBinding,
  ChangeDetectorRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { SocialService } from '../../../../../../../../services/social.service';
import { TripService } from '../../../../../../../../services/trip.service';
import { WaypointService } from '../../../../../../../../services/waypoint.service';
import { AuthService } from '../../../../../../../../core/auth/auth.service';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { SubMenuService } from '../../../../../../../../services/submenu.service';
import { ShareImageService } from '../../../../../../../../services/share-image.service';
import { TripsComponent } from '../../../trips/trips.component';
import { WaypointsComponent } from '../../../waypoints/waypoints.component';
import { ManageMembersComponent } from '../manage-members/manage-members.component';
import { getWaypointIconAsset, resolveWaypointType } from '../../../../../../../../shared/utils/waypoint-icon.util';
import { CreateFleetComponent } from '../create-fleet/create-fleet.component';
import { LoaderComponent } from '../../../../../../reusable/loader/loader.component';
import { ToastService } from '../../../../../../../../services/toast.service';
import { BackButtonComponent } from '../../../../../../../../shared/components/buttons/back-button/back-button.component';
import { ConfirmButtonComponent } from '../../../../../../../../shared/components/buttons/confirm-button/confirm-button.component';
import { CameraIconButtonComponent } from '../../../../../../../../shared/components/buttons/camera-button/camera-button.component';
import { EditButtonComponent } from '../../../../../../../../shared/components/buttons/edit-button/edit-button.component';
import { RestrictiveAcknowledgementDialogComponent } from '../../../../../../../../shared/components/restrictive-acknowledgement-dialog/restrictive-acknowledgement-dialog.component';
import { PostInteractionBarComponent } from '../../../../../../../../features/post-interactions/components/interaction-bar/post-interaction-bar.component';
import { TourAnchorDirective } from '../../../../../../../../features/learning-center/engine/tour-anchor.directive';
import { ImageLightboxComponent } from '../../../../../../../../shared/components/image-lightbox/image-lightbox.component';


@Component({
  selector: 'app-fleet-detail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    ManageMembersComponent,
    CreateFleetComponent,
    LoaderComponent,
    BackButtonComponent,
    ConfirmButtonComponent,
    CameraIconButtonComponent,
    EditButtonComponent,
    TripsComponent,
    WaypointsComponent,
    RestrictiveAcknowledgementDialogComponent,
    PostInteractionBarComponent,
    TourAnchorDirective,
    ImageLightboxComponent
  ],
  templateUrl: './fleet-detail.component.html',
  styleUrl: './fleet-detail.component.scss',
})
export class FleetDetailComponent implements OnInit, OnChanges, AfterViewInit {
  @HostBinding('class.embedded-host') get embeddedHost() { return true; }
  private readonly fishCaughtTabIconMap: Record<string, string> = {
    yellowfin: 'assets/waypoint_icons/caught_yellowfin_tab.png',
    bluefin: 'assets/waypoint_icons/caught_bluefin_tab.png',
    bigeye: 'assets/waypoint_icons/caught_bigeye_tab.png',
    albacore: 'assets/waypoint_icons/caught_albacore_tab.png',
    bluemarlin: 'assets/waypoint_icons/caught_bluemarlin_tab.png',
    whitemarlin: 'assets/waypoint_icons/caught_whitemarlin_tab.png',
    sailfish: 'assets/waypoint_icons/caught_sailfish_tab.png',
    swordfish: 'assets/waypoint_icons/caught_swordfish_tab.png',
    mahi: 'assets/waypoint_icons/caught_mahi_tab.png',
    wahoo: 'assets/waypoint_icons/caught_wahoo_tab.png',
    stripedbass: 'assets/waypoint_icons/caught_stripedbass_tab.png',
    tautog: 'assets/waypoint_icons/caught_tautog_tab.png',
    seabass: 'assets/waypoint_icons/caught_seabass_tab.png',
    fluke: 'assets/waypoint_icons/caught_fluke_tab.png',
    general_tuna: 'assets/waypoint_icons/caught_general_tuna_tab.png',
    general_billfish: 'assets/waypoint_icons/caught_general_billfish_tab.png',
    general_pelagic: 'assets/waypoint_icons/caught_general_pelagic_tab.png',
    general_inshore: 'assets/waypoint_icons/caught_general_inshore_tab.png',
  };

  readonly tripWaypointSummaryIcons: string[] = [
    'assets/waypoint_icons/caught_tab.png',
    'assets/waypoint_icons/spot_tab.svg',
    'assets/home/wavepoint/fog_tab.png',
  ];

  @Input() fleet: any;
  @Input() hideActivity = false;
  @Output() close = new EventEmitter<void>();

  @ViewChild('picInput') picInput!: ElementRef;
  @ViewChild('coverInput') coverInput!: ElementRef;
  @ViewChild('fleetDescriptionText') fleetDescriptionText?: ElementRef<HTMLElement>;

  requesting = false;
  updatingPic = false;
  updatingCover = false;
  showManageMembers = false;
  showEditFleet = false;
  showLeaveConfirm = false;
  showRestrictionRulesDialog = false;
  isDescriptionExpanded = false;
  canToggleDescription = false;

  createdDate: string = '';
  sharedPosts: any[] = [];
  loadingPosts = false;

  lightboxOpen = false;
  lightboxImages: string[] = [];
  lightboxStartIndex = 0;

  /** Posts that actually render in the feed (matches Flutter's filtering). */
  get visiblePosts(): any[] {
    return (this.sharedPosts || []).filter(
      (p) => this.isMemberPost(p) || this.shouldShowTripPost(p) || this.shouldShowWaypointPost(p)
    );
  }
  waypointCountCache: Map<string, number> = new Map();
  waypointCountLoading: Set<string> = new Set();
  private fishIconMap: Record<string, string> = {
    bluefin: '/assets/common_icons/bluefin_icon.svg',
    longfin: '/assets/common_icons/longfin_icon.svg',
    bigeye: '/assets/common_icons/bigeye_icon.svg',
    yellowfin: 'assets/waypoint_icons/yellowfin_icon.svg',
    bluemarlin: '/assets/common_icons/bluemarlin_icon.svg',
    whitemarlin: 'assets/common_icons/whitemarlin_icons.svg'
  };

  public categoryIcons: Record<string, string> = {
    fishing: 'assets/common_icons/fish-food.svg',
    sailing: 'assets/common_icons/sailboat-offshore.svg',
    racing: 'assets/common_icons/ferry-boat.svg',
    cruising: 'assets/common_icons/boat.svg',
    commercial: 'assets/common_icons/store-02.svg',
    research: 'assets/common_icons/search-list-01.svg'
  };
  // ── Restrictive access gate ───────────────────────────────────────────────
  restrictiveAccessRemoved = false;
  checkingRestrictiveAccess = false;

  // ── Trip overlay state ────────────────────────────────────────────────────
  showTripOverlay = false;
  tripViewOnly = false;
  selectedTripData: any = null;
  tripOverlayName = '';
  tripOverlayDescription = '';
  tripOverlayVisibility: 'private' | 'custom' | 'public' = 'private';
  savingTrip = false;
  selectedTripTab: 'trip' | 'waypoint' = 'trip';
  tripWaypoints: any[] = [];
  loadingWaypoints = false;

  // ── Expanded trip posts (show more / show less) ───────────────────────────
  expandedTripPosts = new Set<string>();

  // ── Send trip state ───────────────────────────────────────────────────────
  isSendTripOpen = false;
  activeSendTrip: any = null;
  sendTripSearchQuery = '';
  sendFleets: any[] = [];
  sendDockmates: any[] = [];

  fleetTripId: string | null = null;
  fleetWaypointId: string | null = null;
  selectedFleetWaypointData: any = null;

  // ── Share image sheet ─────────────────────────────────────────────────────
  shareImageSheetOpen = false;
  shareImagePost: any = null;
  shareImageStep: 'choose' | 'caption' | 'generating' | 'generated' = 'choose';
  shareImageFile: File | null = null;
  shareImagePreviewUrl: string | null = null;
  shareImageCaption = '';
  shareImagePreparationError = '';
  shareImageGeneratedFile: File | null = null;
  shareImageGeneratedPreviewUrl: string | null = null;

  constructor(
    private socialService: SocialService,
    private tripService: TripService,
    private waypointService: WaypointService,
    private authService: AuthService,
    private submenuService: SubMenuService,
    private cdr: ChangeDetectorRef,
    private shareImageService: ShareImageService,
    public toast: ToastService,
    private router: Router,
  ) { }

  /**
   * A member row in the roster was tapped — open that captain's full profile.
   * Works for every role: browsing the roster is not an admin privilege.
   */
  onMemberSelected(member: any): void {
    const userId = String(member?.userId || '').trim();
    if (!userId) return;
    this.closeManageMembers();
    this.router.navigate(['/dockmates', userId, 'full-profile']);
  }

  // NOTE: there is deliberately no `openCaptainProfile()` / owner-id lookup here.
  // The app renders "Captain <name>" as a plain blue `Text` in
  // `fleet_details_screen.dart` with no tap target, and its `FleetModel` has no
  // owner id to navigate with. Making the web label a link would diverge from the
  // app and require an owner id the fleets LIST endpoint does not send. The
  // roster is reached through the member count, exactly as in the app.

  ngOnInit() {
    this.checkAndFetchCreatedDate();
  }

  ngAfterViewInit() {
    this.updateDescriptionToggleState();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['fleet']) {
      this.checkAndFetchCreatedDate();
      this.checkRestrictiveAccess();
      this.fetchSharedPosts();
      this.isDescriptionExpanded = false;
      this.updateDescriptionToggleState();
    }
  }

   private normalizeFishQuantity(value: number): number {
    if (!Number.isFinite(value)) return 1;
    return Math.max(1, Math.min(99, Math.trunc(value)));
  }

   getFishQuantityBadge(wp: any): string | null {
    // Fleet post snapshots don't always store `type` literally as 'fish_caught'
    // (it can live under waypoint_type/event_type or only be inferable from the
    // name), so resolve it the same robust way the card icon does.
    if (!this.isFishCaughtType(wp)) return null;
    const rawQty = wp?.details?.fish_quantity
      ?? wp?.fish_quantity
      ?? wp?.fish_details?.fish_quantity
      ?? wp?.waypoint_details?.details?.fish_quantity
      ?? 1;
    const qty = this.normalizeFishQuantity(Number(rawQty || 1));
    if (qty <= 1) return null;
    return `x${qty}`;
  }

  // Mirrors Flutter's `canManageMembers = isOwner || isAdmin`
  // (fleet_details_screen.dart). Owners/admins get the manage view (add +
  // remove); everyone else still gets the member list, but read-only with a
  // role badge — the member count is clickable for all.
  get canManageMembers(): boolean {
    const role = String(this.fleet?.my_role || '').toLowerCase();
    return role === 'owner' || role === 'admin';
  }

  /** True once the viewer has actually joined (or owns) the fleet. */
  get isFleetMember(): boolean {
    const role = String(this.fleet?.my_role || '').toLowerCase();
    if (role === 'owner' || role === 'admin' || role === 'member') return true;
    return String(this.fleet?.request_status || '').toLowerCase() === 'accepted';
  }

  /** Home port as the backend stores it — no placeholder, no appended state. */
  get fleetHomePort(): string {
    return String(this.fleet?.home_port ?? '').trim();
  }

  private checkRestrictiveAccess() {
    // Mirror the Flutter source of truth (fleet_details_screen.dart
    // _buildSharedPostsSection): in the shared-posts section a restrictive
    // fleet always renders the "Access Removed" state — there the gate is
    // simply `restrictiveAccessRemoved = effectiveRestrictive` (i.e.
    // `is_restrictive`). The old per-trip / owner-admin check made the web
    // keep showing posts when the app had already removed access.
    this.checkingRestrictiveAccess = false;
    this.restrictiveAccessRemoved = !!this.fleet?.is_restrictive;
  }

  toggleDescription(): void {
    if (!this.canToggleDescription) return;
    this.isDescriptionExpanded = !this.isDescriptionExpanded;
  }

  private updateDescriptionToggleState(): void {
    setTimeout(() => {
      const el = this.fleetDescriptionText?.nativeElement;
      if (!el) {
        this.canToggleDescription = false;
        return;
      }
      this.canToggleDescription = el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight;
    });
  }

  private fetchSharedPosts() {
    if (!this.fleet?._id) return;
    this.loadingPosts = true;
    this.sharedPosts = [];
    this.loadSharedPostsPage(this.fleet._id, undefined);
  }

  private loadSharedPostsPage(fleetId: string, before: string | undefined) {
    this.socialService.getSharedPosts(fleetId, before).subscribe({
      next: (res: any) => {
        const page: any[] = res.posts || res.results || res.data || res.items || [];
        this.sharedPosts = [...this.sharedPosts, ...page];

        const nextCursor: string | null = res.next_cursor ?? null;
        if (nextCursor && page.length > 0) {
          // More pages available — load next
          this.loadSharedPostsPage(fleetId, nextCursor);
        } else {
          // All pages loaded — sort descending by create_ts
          this.sharedPosts.sort((a: any, b: any) => {
            const aTs = a.audit_data?.create_ts || a.audit_data?.created_at || '';
            const bTs = b.audit_data?.create_ts || b.audit_data?.created_at || '';
            return new Date(bTs).getTime() - new Date(aTs).getTime();
          });
          this.loadingPosts = false;
          this.cdr.detectChanges();
          this.fetchWaypointCountsForTrips();
        }
      },
      error: (err) => {
        console.error('Error fetching shared posts:', err);
        this.loadingPosts = false;
        this.cdr.detectChanges();
      },
    });
  }

  /**
   * Resolves a trip's id from a fleet post. Fleet post snapshots often omit
   * `trip_details._id` and carry the id on `trip_ref` instead (mirrors how
   * `openTripDetail` resolves it), so we fall back through both.
   */
  private getPostTripId(post: any): string | null {
    const tripRef = post?.trip_ref || {};
    const tripId = post?.trip_details?._id
      || post?.trip_details?.id
      || tripRef['reference id']
      || tripRef.reference_id
      || tripRef.identifier;
    return tripId ? String(tripId) : null;
  }

  private fetchWaypointCountsForTrips() {
    const tripPosts = this.sharedPosts.filter((p: any) => this.isTripPost(p));
    tripPosts.forEach((post: any) => {
      const tripId = this.getPostTripId(post);
      if (!tripId || this.waypointCountCache.has(tripId)) return;
      this.waypointCountLoading.add(tripId);
      this.tripService.getWaypointCountByTrip(tripId).subscribe({
        next: (count: number) => {
          this.waypointCountCache.set(tripId, count);
          this.waypointCountLoading.delete(tripId);
          this.cdr.detectChanges();
        },
        error: () => {
          this.waypointCountCache.set(tripId, 0);
          this.waypointCountLoading.delete(tripId);
          this.cdr.detectChanges();
        },
      });
    });
  }

  getVisibleWaypointCount(post: any): number {
    const tripId = this.getPostTripId(post);
    if (tripId && this.waypointCountCache.has(tripId)) {
      return this.waypointCountCache.get(tripId)!;
    }
    return this.getTripWaypointCountFallback(post.trip_details) ?? 0;
  }

  isVisibleWaypointCountLoading(post: any): boolean {
    const tripId = this.getPostTripId(post);
    return this.getTripWaypointCountFallback(post.trip_details) == null && !!tripId && this.waypointCountLoading.has(tripId);
  }

  private getTripWaypointCountFallback(tripDetails: any): number | null {
    const candidates = [
      typeof tripDetails?.metrics?.waypoint_count === 'number' ? tripDetails.metrics.waypoint_count : null,
      typeof tripDetails?.waypoint_count === 'number' ? tripDetails.waypoint_count : null,
      Array.isArray(tripDetails?.waypoint_ids) ? tripDetails.waypoint_ids.length : null,
      Array.isArray(tripDetails?.waypoints) ? tripDetails.waypoints.length : null,
    ].filter((value): value is number => typeof value === 'number' && Number.isFinite(value));

    return candidates.length ? Math.max(...candidates) : null;
  }

  isWaypointPost(post: any): boolean {
    const t = (post?.post_type || '').toLowerCase();
    return t === 'waypoint' || t === 'post' || t === 'moment' || t === 'photo';
  }

  isPhotoPost(post: any): boolean {
    if (!post) return false;
    const type = String(
      post?.post_type ||
      post?.waypoint_details?.waypoint_type ||
      post?.waypoint_details?.type ||
      post?.type ||
      ''
    ).trim().toLowerCase();
    if (type === 'moment' || type === 'photo' || type === 'photos') return true;
    const name = String(
      post?.waypoint_details?.name ||
      post?.name ||
      post?.content?.headline ||
      ''
    ).trim().toLowerCase();
    return name === 'photo';
  }

  isTextPost(post: any): boolean {
    if (!post) return false;
    const type = String(
      post?.post_type ||
      post?.waypoint_details?.waypoint_type ||
      post?.waypoint_details?.type ||
      post?.type ||
      ''
    ).trim().toLowerCase();
    if (type === 'post') return true;
    const name = String(
      post?.waypoint_details?.name ||
      post?.name ||
      post?.content?.headline ||
      ''
    ).trim().toLowerCase();
    return name === 'post';
  }

  getPostText(post: any): string {
    const desc = (
      post?.waypoint_details?.description ||
      post?.description ||
      post?.content?.text ||
      post?.notes ||
      ''
    ).trim();
    if (desc && !/published a new waypoint/i.test(desc) && !/published a new/i.test(desc)) return desc;
    const name = (
      post?.waypoint_details?.name ||
      post?.name ||
      post?.content?.headline ||
      ''
    ).trim();
    if (name && name.toLowerCase() !== 'post' && !/published a new waypoint/i.test(name)) {
      return name;
    }
    return '';
  }

  getPostCaption(post: any): string {
    const desc = (
      post?.waypoint_details?.description ||
      post?.description ||
      post?.content?.text ||
      post?.notes ||
      ''
    ).trim();
    if (!desc || /published a new waypoint/i.test(desc) || /published a new/i.test(desc)) {
      return '';
    }
    return desc;
  }

  getPhotoCoordinates(post: any): string {
    const coords = this.getFullCoordinates(post?.waypoint_details?.position || post?.position);
    if (!coords || coords === 'N/A' || coords.trim() === '') {
      return '';
    }
    return coords;
  }

  getBadgeType(post: any): string {
    const privacy = (post?.waypoint_details?.privacy || post?.trip_details?.privacy || post?.privacy || 'public').toLowerCase();
    if (privacy === 'custom' || privacy === 'friends' || privacy === 'dockmates' || privacy === 'shared') return 'custom';
    if (privacy === 'private') return 'private';
    return 'public';
  }

  getBadgeLabel(post: any): string {
    if (!this.canEditPost(post)) return 'Shared';
    const type = this.getBadgeType(post);
    if (type === 'private') return 'Private';
    if (type === 'custom') return 'Custom';
    return 'Public';
  }

  getAuthorName(post: any): string {
    return post?.source_profile?.name || post?.author_name || 'Captain';
  }

  getAuthorAvatar(post: any): string | null {
    return post?.source_profile?.image_url || post?.author_avatar_url || null;
  }

  getAuthorInitials(post: any): string {
    const name = this.getAuthorName(post);
    if (!name) return 'C';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  }

  onPostAvatarClick(post: any, event: Event): void {
    event.stopPropagation();
    const userId = post?.source_profile?.user_id
      || post?.source_profile?.identifier
      || post?.source_profile?._id
      || post?.source_profile?.id
      || post?.audit_data?.create_user_id;
    if (userId) {
      this.closePopup();
      this.router.navigate(['/dockmates', String(userId), 'full-profile']);
    }
  }

  getPostId(post: any): string {
    return String(post?._id || post?.id || post?.waypoint_details?._id || post?.waypoint_details?.waypoint_id || post?.identifier || '');
  }

  getPostCommentsCount(post: any): number {
    return (
      post?.comments_count ??
      post?.comment_count ??
      post?.reactions?.comment_count ??
      post?.interactions?.comment_count ??
      0
    );
  }

  onImageError(event: Event): void {
    const target = event.target as HTMLElement;
    if (target) {
      target.style.display = 'none';
    }
  }

  openPhotoLightbox(post: any, clickedUrl?: string, event?: Event): void {
    if (event) event.stopPropagation();
    const images = this.getPostImages(post);
    if (!images || images.length === 0) {
      if (clickedUrl) {
        this.lightboxImages = [clickedUrl];
        this.lightboxStartIndex = 0;
        this.lightboxOpen = true;
      }
      return;
    }
    this.lightboxImages = images;
    const idx = clickedUrl ? images.indexOf(clickedUrl) : 0;
    this.lightboxStartIndex = idx >= 0 ? idx : 0;
    this.lightboxOpen = true;
  }

  closeLightbox(): void {
    this.lightboxOpen = false;
  }

  shouldShowWaypointPost(post: any): boolean {
    if (this.isTextPost(post) || this.isPhotoPost(post)) {
      const details = post.waypoint_details || post;
      const privacy = (details?.privacy || post.privacy || '').toString().toLowerCase();
      if (privacy === 'private' && !this.canEditPost(post)) return false;
      return true;
    }
    if (!this.isWaypointPost(post)) return false;
    const details = post.waypoint_details || post;
    if (!details) return false;
    // Match Flutter (_willRenderFleetPost): default privacy is "private",
    // hide public waypoints, and hide private waypoints that aren't mine.
    const privacy = (details.privacy || post.privacy || '').toString().toLowerCase() || 'private';
    if (privacy === 'public') return false;
    if (privacy === 'private' && !this.canEditPost(post)) return false;
    return true;
  }

  isTripPost(post: any): boolean {
    const t = (post.post_type || '').toLowerCase();
    return t === 'trips' || t === 'trip';
  }

  // Mirrors Flutter's _willRenderFleetPost trip rule: only "custom" trips
  // (and private trips that are mine) render in the fleet feed. Public trips
  // — e.g. the empty "FleetShare" posts — are hidden, matching the app.
  shouldShowTripPost(post: any): boolean {
    if (!this.isTripPost(post) || this.isMemberPost(post)) return false;
    const td = post.trip_details;
    if (!td) return false;
    const privacy = (td.privacy || '').toString().toLowerCase() || 'public';
    if (privacy === 'public') return false;
    if (privacy === 'private' && !this.canEditPost(post)) return false;
    return true;
  }

  isMemberPost(post: any): boolean {
    if (!post) return false;
    return post.post_type === 'fleets' && post.action_type === 'member_joined';
  }

  isSpecializedWaypoint(post: any): boolean {
    const name = (post.waypoint_details?.name || post.waypoint_details?.type || '').toLowerCase();
    return name.includes('fish mark') || name.includes('fish_mark') || name.includes('fish-mark') ||
      name.includes('bait mark') || name.includes('bait_mark') || name.includes('bait-mark') ||
      name.includes('temp break') || name.includes('temp_break') || name.includes('fish caught') ||
      name.includes('fish_caught') || name.includes('fish-caught');
  }

  openTripDetail(post: any, viewOnly = false) {
    const tripRef = post.trip_ref || {};
    const tripId = post.trip_details?._id
      || tripRef['reference id']
      || tripRef.reference_id
      || tripRef.identifier;
    if (!tripId) return;

    this.submenuService.readOnlyTripMode = viewOnly || !this.isOwnTripPost(post);
    if (post.trip_details) {
      this.submenuService.pendingTripData = { ...post.trip_details, _id: tripId };
    }
    this.fleetTripId = tripId;
  }

  private isOwnTripPost(post: any): boolean {
    const userId = this.authService.getUserObjectId();
    const authId = this.authService.getUserIdFromToken();
    const creatorId = post.audit_data?.create_user_id
      || post.source_profile?._id
      || post.source_profile?.id
      || post.trip_details?.audit_data?.create_user_id;

    if (!creatorId) return false;
    return creatorId === userId || creatorId === authId;
  }

  openWaypointDetail(post: any): void {
    const waypointId = post?.waypoint_details?.waypoint_id
      || post?.waypoint_details?._id
      || post?.waypoint_ref?.identifier
      || post?._id
      || post?.id;
    if (!waypointId) return;
    this.selectedFleetWaypointData = post?.waypoint_details || post;
    this.fleetWaypointId = waypointId;
  }

  openSharedWaypointDetail(post: any, event?: Event): void {
    event?.stopPropagation();
    this.openWaypointDetail(post);
  }

  openSharedTripDetail(post: any, event?: Event): void {
    event?.stopPropagation();
    this.openTripDetail(post, true);
  }

  selectTripTab(tab: 'trip' | 'waypoint') {
    this.selectedTripTab = tab;
    if (tab === 'waypoint' && this.tripWaypoints.length === 0 && this.selectedTripData?._id) {
      this.loadingWaypoints = true;
      this.tripService.getWaypointsByTrip(this.selectedTripData._id).subscribe({
        next: (res: any) => {
          this.tripWaypoints = res?.waypoints || res?.results || res || [];
          this.loadingWaypoints = false;
        },
        error: () => { this.loadingWaypoints = false; }
      });
    }
  }

  closeTripOverlay() {
    this.showTripOverlay = false;
    this.selectedTripData = null;
    this.tripViewOnly = false;
  }

  saveTripOverlay() {
    if (!this.selectedTripData?._id || this.savingTrip) return;
    this.savingTrip = true;
    const formData = new FormData();
    formData.append('name', this.tripOverlayName || '');
    if (this.tripOverlayDescription != null) {
      formData.append('description', this.tripOverlayDescription.trim());
    }
    formData.append('privacy', this.tripOverlayVisibility);

    this.tripService.updateTrip(this.selectedTripData._id, formData).subscribe({
      next: () => { this.savingTrip = false; this.closeTripOverlay(); },
      error: (err) => { console.error('Error saving trip:', err); this.savingTrip = false; }
    });
  }

  deleteTripOverlay() {
    if (!this.selectedTripData?._id) return;
    if (!confirm('Are you sure you want to delete this trip?')) return;
    this.tripService.deleteTrip(this.selectedTripData._id).subscribe({
      next: () => this.closeTripOverlay(),
      error: (err) => console.error('Error deleting trip:', err)
    });
  }

  cyclePrivacy() {
    const order: Array<'private' | 'custom' | 'public'> = ['private', 'custom', 'public'];
    const idx = order.indexOf(this.tripOverlayVisibility);
    this.tripOverlayVisibility = order[(idx + 1) % order.length];
  }

  private isObjectId(s: string): boolean {
    return /^[0-9a-f]{24}$/i.test((s || '').trim());
  }

  getTripDisplayName(post: any): string {
    const name = post.trip_details?.name?.trim();
    if (name && !this.isObjectId(name)) return name;
    const identifier = post.trip_ref?.identifier?.trim();
    if (identifier && !this.isObjectId(identifier)) return identifier;
    return 'FleetShare';
  }

  /** Returns true if the trip has a real display‑worthy name (not an ObjectId) */
  hasRealTripName(post: any): boolean {
    const name = post.trip_details?.name?.trim();
    if (name && !this.isObjectId(name)) return true;
    const identifier = post.trip_ref?.identifier?.trim();
    if (identifier && !this.isObjectId(identifier)) return true;
    return false;
  }

  normalizeTripState(state: string | undefined): string {
    if (!state) return 'complete';
    const s = state.toLowerCase().replace(/\s+/g, '_');
    if (s === 'completed') return 'complete';
    return s;
  }

  getTripStateLabel(state: string | undefined): string {
    const s = this.normalizeTripState(state);
    if (s === 'complete') return 'Complete';
    if (s === 'in_progress') return 'In Progress';
    if (s === 'planned') return 'Planned';
    return s.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }

  getTripPrivacyDisplay(p: string): string {
    if (p === 'public') return 'The Water (Everyone)';
    if (p === 'custom') return 'Pier Circle (Dockmates and Fleets)';
    return 'Private (Only Me)';
  }

  getRelativeTime(timestamp: string): string {
    if (!timestamp) return '';
    const date = new Date(timestamp.endsWith('Z') ? timestamp : timestamp + 'Z');
    const now = new Date();
    const diffSec = Math.floor((now.getTime() - date.getTime()) / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);
    const diffWeek = Math.floor(diffDay / 7);

    if (diffMin < 1) return 'now';
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHour < 24) return `${diffHour}h ago`;
    if (diffDay < 7) return `${diffDay}d ago`;
    return `${diffWeek}w ago`;
  }

  calculateDuration(start: string, end: string, tripState?: string, rawDurationHours?: number): string {
    const toUtcMs = (ts: string) => new Date(ts.endsWith('Z') ? ts : ts + 'Z').getTime();
    const formatHM = (mins: number) => {
      if (mins <= 0) return '0m';
      const h = Math.floor(mins / 60), m = mins % 60;
      return h > 0 ? `${h}h ${m}m` : `${m}m`;
    };
    const state = (tripState || '').toLowerCase();
    const isInProgress = state === 'in_progress' || state === 'paused' || state === 'stop_proposed';
    if (isInProgress && start) {
      const mins = Math.floor((Date.now() - toUtcMs(start)) / 60000);
      return mins > 0 ? formatHM(mins) : '< 1m';
    }
    if (start && end) {
      const mins = Math.floor((toUtcMs(end) - toUtcMs(start)) / 60000);
      if (mins > 0) return formatHM(mins);
    }
    if (rawDurationHours != null && rawDurationHours > 0)
      return formatHM(Math.round(rawDurationHours * 60));
    return '--';
  }

  formatDateRange(start: string): string {
    if (!start) return '--';
    const d = new Date(start);
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yy = String(d.getFullYear()).slice(-2);
    return `${mm}/${dd}/${yy}`;
  }

  getTripDistance(dist: any): string {
    if (dist === undefined || dist === null) return '0.00 nmi';
    const value = Math.floor(Number(dist) * 100) / 100;
    return `${value.toFixed(1)} nmi`;
  }

  getTripMapUrl(trip: any): string {
    const key = 'IfvuVnjKOAzdw0Wrs5eg';
    const mapId = '019adf7b-1688-7ff4-ae90-c50ef00338f9';
    const width = 600;
    const height = 200;

    let pathStr = '';
    const trackData = trip?.track_simplified || trip?.track;
    if (trackData?.features?.length > 0) {
      let coords = trackData.features
        .map((f: any) => {
          const c = f.geometry?.coordinates;
          return c ? `${c[0]},${c[1]}` : null;
        })
        .filter((c: any) => c !== null);
      if (coords.length > 60) {
        const step = Math.floor(coords.length / 60);
        coords = coords.filter((_: any, i: number) => i % step === 0).slice(0, 60);
      }
      if (coords.length > 0) {
        pathStr = `fill:none|stroke:blue|width:4|${coords.join('|')}`;
      }
    }

    if (!pathStr) {
      const startPos = this.extractPosDetails(trip?.start_position);
      const endPos = this.extractPosDetails(trip?.end_position);
      if (startPos && endPos) {
        if (startPos.lat === endPos.lat && startPos.lng === endPos.lng) {
          return `https://api.maptiler.com/maps/${mapId}/static/${startPos.lng},${startPos.lat},12/${width}x${height}.png?key=${key}`;
        }
        pathStr = `fill:none|stroke:blue|width:4|${startPos.lng},${startPos.lat}|${endPos.lng},${endPos.lat}`;
      } else {
        const pos = startPos || endPos;
        if (pos) {
          return `https://api.maptiler.com/maps/${mapId}/static/${pos.lng},${pos.lat},12/${width}x${height}.png?key=${key}`;
        }
      }
    }

    if (pathStr) {
      return `https://api.maptiler.com/maps/${mapId}/static/auto/${width}x${height}.png?key=${key}&padding=0.25&path=${encodeURIComponent(pathStr)}`;
    }
    return '';
  }

  extractPosDetails(pos: any): { lat: number; lng: number } | null {
    if (!pos) return null;
    if (pos.latitude != null && pos.longitude != null) {
      return { lat: Number(pos.latitude), lng: Number(pos.longitude) };
    }
    if (pos.lat != null && pos.lon != null) {
      return { lat: Number(pos.lat), lng: Number(pos.lon) };
    }
    if (Array.isArray(pos.coordinates) && pos.coordinates.length >= 2 &&
      pos.coordinates[0] != null && pos.coordinates[1] != null) {
      return { lat: Number(pos.coordinates[1]), lng: Number(pos.coordinates[0]) };
    }
    return null;
  }

  hasCoordinates(pos: any): boolean {
    return !!this.extractPosDetails(pos);
  }

  hasObservationSummary(wp: any): boolean {
    const obs = wp?.observation_summary;
    if (!obs) return false;
    return obs.sog != null || obs.cog != null || obs.temp != null || obs.depth != null || obs.air_temperature != null || obs.wind_speed != null;
  }

  getPostImages(post: any): string[] {
    const urls: string[] = [];
    const candidates = [
      post?.image_urls,
      post?.images,
      post?.waypoint_details?.image_urls,
      post?.waypoint_details?.images,
      post?.waypoint_details?.details?.image_urls,
      post?.waypoint_details?.details?.images,
      post?.content?.image_urls,
      post?.content?.images,
    ];
    for (const item of candidates) {
      if (Array.isArray(item)) {
        for (const u of item) {
          const url = typeof u === 'string' ? u : (u?.image_url || u?.imageUrl || u?.url);
          if (typeof url === 'string' && url.trim().length > 0 && !urls.includes(url.trim())) {
            urls.push(url.trim());
          }
        }
      } else if (typeof item === 'string' && item.trim().length > 0 && !urls.includes(item.trim())) {
        urls.push(item.trim());
      }
    }
    return urls;
  }

  private checkAndFetchCreatedDate() {
    if (this.fleet?.audit_data?.create_ts) {
      this.createdDate = this.formatDetailedDate(this.fleet.audit_data.create_ts);
    } else if (this.fleet?._id) {
      this.getCreatedDate();
    }
  }

  closePopup() {
    this.close.emit();
  }

  // ── Expanded trip posts ───────────────────────────────────────────────────
  isTripPostExpanded(id: string): boolean {
    return this.expandedTripPosts.has(id);
  }

  toggleTripPostExpanded(id: string, event: Event) {
    event.stopPropagation();
    if (this.expandedTripPosts.has(id)) {
      this.expandedTripPosts.delete(id);
    } else {
      this.expandedTripPosts.add(id);
    }
  }

  // ── Send trip ─────────────────────────────────────────────────────────────
  private getFleetTripId(post: any): string | null {
    return post?.trip_details?._id || post?.trip_details?.id
      || post?.trip_ref?.identifier || post?.trip_ref?.reference_id || null;
  }

  openSendTrip(post: any, event: Event) {
    event.stopPropagation();
    this.activeSendTrip = post;
    this.sendTripSearchQuery = '';
    this.isSendTripOpen = true;

    const tripId = this.getFleetTripId(post);
    forkJoin([
      this.waypointService.getFleets(),
      this.socialService.myDockmates(1, 100),
      tripId
        ? this.tripService.getTripSharedWith(tripId).pipe(catchError(() => of({ shared_with: { friends: [], fleets: [] } })))
        : of({ shared_with: { friends: [], fleets: [] } }),
    ]).subscribe({
      next: ([fleetsRes, friendsRes, sharedWith]: any[]) => {
        const sw = sharedWith?.shared_with || sharedWith || {};
        const sharedFleetIds: string[] = (sw.fleets || []).map((f: any) => f.community_id).filter(Boolean);
        const sharedFriendIds: string[] = (sw.friends || []).map((f: any) => f.user_id).filter(Boolean);

        this.sendFleets = (fleetsRes.results || []).map((f: any) => ({
          ...f,
          sendSelected: false,
          alreadyShared: sharedFleetIds.includes(f.id) || sharedFleetIds.includes(f._id) || sharedFleetIds.includes(f.community_id),
        }));

        const tokenUserId = this.authService.getUserIdFromToken();
        const objUserId = this.authService.getUserObjectId();
        const results = (friendsRes.results || []).filter((m: any) => {
          return m.user?._id !== objUserId && m.user?.user_id !== tokenUserId;
        });
        this.sendDockmates = results.map((m: any) => ({
          ...m,
          name: m.user?.name || m.name || '',
          image_url: m.user?.image_url || m.image_url || '',
          sendSelected: false,
          alreadyShared: sharedFriendIds.includes(m.user?._id),
        }));
      },
    });
  }

  closeSendTrip() {
    this.isSendTripOpen = false;
    this.activeSendTrip = null;
  }

  confirmSendTrip() {
    if (!this.activeSendTrip) return;
    const tripId = this.getFleetTripId(this.activeSendTrip);
    if (!tripId) return;

    const newFriendIds = this.sendDockmates.filter((m) => m.sendSelected);
    const newFleetIds = this.sendFleets.filter((f) => f.sendSelected);
    if (newFriendIds.length === 0 && newFleetIds.length === 0) { this.closeSendTrip(); return; }

    const friend_ids = this.sendDockmates
      .filter((m) => m.sendSelected || m.alreadyShared)
      .map((m) => m.user?._id)
      .filter(Boolean);
    const fleet_ids = this.sendFleets
      .filter((f) => f.sendSelected || f.alreadyShared)
      .map((f) => f._id);

    this.tripService.shareTrip(tripId, { friend_ids, fleet_ids, message: '' }).subscribe({
      next: () => this.closeSendTrip(),
      error: () => this.closeSendTrip(),
    });
  }

  shareTrip(post: any, event: Event) {
    event.stopPropagation();
    this.openShareImageSheet(post);
  }

  openShareImageSheet(post: any) {
    this.shareImagePost = post;
    this.shareImageStep = 'choose';
    this.shareImageFile = null;
    if (this.shareImagePreviewUrl) URL.revokeObjectURL(this.shareImagePreviewUrl);
    this.shareImagePreviewUrl = null;
    this.shareImageCaption = '';
    this.shareImagePreparationError = '';
    if (this.shareImageGeneratedPreviewUrl) URL.revokeObjectURL(this.shareImageGeneratedPreviewUrl);
    this.shareImageGeneratedFile = null;
    this.shareImageGeneratedPreviewUrl = null;
    this.shareImageSheetOpen = true;
  }

  closeShareImageSheet() {
    if (this.shareImagePreviewUrl) URL.revokeObjectURL(this.shareImagePreviewUrl);
    if (this.shareImageGeneratedPreviewUrl) URL.revokeObjectURL(this.shareImageGeneratedPreviewUrl);
    this.shareImageSheetOpen = false;
    this.shareImagePost = null;
    this.shareImageStep = 'choose';
    this.shareImageFile = null;
    this.shareImagePreviewUrl = null;
    this.shareImageCaption = '';
    this.shareImagePreparationError = '';
    this.shareImageGeneratedFile = null;
    this.shareImageGeneratedPreviewUrl = null;
  }

  shareImageGoBack() {
    if (this.shareImageStep === 'caption') {
      if (this.shareImagePreviewUrl) URL.revokeObjectURL(this.shareImagePreviewUrl);
      this.shareImageFile = null;
      this.shareImagePreviewUrl = null;
      this.shareImageStep = 'choose';
    } else if (this.shareImageStep === 'generated') {
      if (this.shareImageGeneratedPreviewUrl) { URL.revokeObjectURL(this.shareImageGeneratedPreviewUrl); this.shareImageGeneratedPreviewUrl = null; }
      this.shareImageGeneratedFile = null;
      this.shareImageStep = 'caption';
    } else {
      this.closeShareImageSheet();
    }
  }

  onShareImageSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    if (this.shareImagePreviewUrl) URL.revokeObjectURL(this.shareImagePreviewUrl);
    this.shareImageFile = file;
    this.shareImagePreviewUrl = URL.createObjectURL(file);
    this.shareImageStep = 'caption';
    this.cdr.detectChanges();
  }

  async generateShareCard() {
    if (!this.shareImageFile || this.shareImageStep !== 'caption') return;
    const caption = this.shareImageCaption.trim();
    const file = this.shareImageFile;
    this.shareImageStep = 'generating';
    this.cdr.detectChanges();
    await new Promise<void>(resolve => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
    try {
      const [generated] = await Promise.all([
        this.shareImageService.prepareShareImageForUpload(file, caption),
        new Promise<void>(resolve => setTimeout(resolve, 1500)),
      ]);
      this.shareImageGeneratedFile = generated;
      if (this.shareImageGeneratedPreviewUrl) {
        URL.revokeObjectURL(this.shareImageGeneratedPreviewUrl);
      }
      this.shareImageGeneratedPreviewUrl = URL.createObjectURL(generated);
      this.shareImageStep = 'generated';
      this.cdr.detectChanges();
    } catch {
      this.shareImageStep = 'caption';
      this.cdr.detectChanges();
    }
  }

  async confirmShareCard() {
    if (!this.shareImageGeneratedFile) return;
    const post = this.shareImagePost;
    const title = post?.trip_details?.name || post?.waypoint_details?.name || 'Post';
    const result = await this.shareImageService.shareFile(this.shareImageGeneratedFile, title);
    if (result === 'cancelled') return;
    if (result === 'download') {
      this.toast.success('Image downloaded');
    }
    this.closeShareImageSheet();
  }

  get filteredSendFleets() {
    if (!this.sendTripSearchQuery) return this.sendFleets;
    return this.sendFleets.filter((f) =>
      f.name?.toLowerCase().includes(this.sendTripSearchQuery.toLowerCase())
    );
  }

  get filteredSendDockmates() {
    if (!this.sendTripSearchQuery) return this.sendDockmates;
    return this.sendDockmates.filter((m) =>
      m.name?.toLowerCase().includes(this.sendTripSearchQuery.toLowerCase())
    );
  }

  // ── Display helpers ───────────────────────────────────────────────────────
  cleanTimeValue(val: string | null | undefined): string {
    if (!val) return '--';
    return val.trim().replace(/\s+DT$/i, '');
  }

  getDistance(dist: any): string {
    if (dist === undefined || dist === null || isNaN(Number(dist))) return '0 m';
    const nmi = Number(dist);
    if (nmi < 0.1) return `${Math.round(nmi * 1852)} m`;
    return `${nmi.toFixed(1)} nmi`;
  }

  openMemberList() {
    this.showManageMembers = true;
  }

  closeManageMembers() {
    this.showManageMembers = false;
  }

  openEditFleet() {
    this.showEditFleet = true;
  }

  closeEditFleet() {
    this.showEditFleet = false;
  }

  triggerUpdate(type: 'pic' | 'cover') {
    if (this.picInput && type === 'pic') this.picInput.nativeElement.click();
    if (this.coverInput && type === 'cover')
      this.coverInput.nativeElement.click();
  }

  onFileSelected(event: any, type: 'pic' | 'cover') {
    const file = event.target.files[0];
    if (file && this.fleet?._id) {
      if (type === 'pic') this.updatingPic = true;
      else this.updatingCover = true;

      const formData = new FormData();
      if (type === 'pic') {
        formData.append('community_image', file);
      } else {
        formData.append('community_cover', file);
      }

      this.socialService.updateFleet(this.fleet._id, formData).subscribe({
        next: (res: any) => {
          if (type === 'pic') {
            this.fleet.image_url = res.image_url;
            this.updatingPic = false;
          } else {
            this.fleet.cover_photo_url = res.cover_photo_url;
            this.updatingCover = false;
          }
          this.toast.success(type === 'pic' ? 'Fleet photo updated' : 'Cover photo updated');
        },
        error: (err: any) => {
          console.error(`Error updating ${type}:`, err);
          if (type === 'pic') this.updatingPic = false;
          else this.updatingCover = false;
          this.toast.error(type === 'pic' ? 'Failed to update fleet photo' : 'Failed to update cover photo');
        },
      });
    }
  }
  getCreatedDate() {
    this.socialService.getCommunityById(this.fleet._id).subscribe({
      next: (res: any) => {
        this.createdDate = res?.audit_data?.create_ts
          ? this.formatDetailedDate(res.audit_data.create_ts)
          : '';
      },
      error: (err) => {
        console.error('Error fetching community:', err);
      },
    });
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date
      .toLocaleString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        day: 'numeric',
        month: 'numeric',
        year: '2-digit',
      })
      .replace(',', '');
  }

  formatDetailedDate(dateStr: string): string {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  }

  getTempUnitSymbol(unit: string | undefined): string {
    if (!unit) return '';
    const u = unit.toLowerCase();
    if (u === 'fahrenheit') return '°F';
    if (u === 'celsius') return '°C';
    return unit;
  }

  getUnitSymbol(unit: string | undefined): string {
    if (!unit) return '';
    const u = unit.toLowerCase();
    switch (u) {
      case 'fahrenheit': return 'F';
      case 'celsius': return 'C';
      case 'feet': return 'ft';
      case 'meters': return 'm';
      case 'mph': return 'mph';
      case 'kts':
      case 'knots': return 'kts';
      default: return unit;
    }
  }

  getWaypointHeading(subtype: string): string {
    if (!subtype) return 'Waypoint';
    return (
      subtype.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()) +
      ' Caught'
    );
  }

  getWaypointDisplayName(wp: any): string {
    if (!wp) return 'Waypoint';

    const name = String(wp.name || wp.waypoint_name || '').toLowerCase();
    const resolvedType = resolveWaypointType(wp);
    const fishSubtype =
      wp?.details?.fish_subtype ||
      wp?.details?.fish_subtype ||
      wp?.details?.subtype ||
      wp?.fish_subtype ||
      wp?.subtype;

    const hasFishDetails = !!(
      wp?.fish_details?.fish_subtype ||
      wp?.details?.fish_subtype ||
      wp?.details?.fish_category ||
      wp?.details?.fish_quantity
    );
    const isFishCaught = resolvedType === 'fish_caught' ||
      name.includes('caught') ||
      hasFishDetails;

    if (isFishCaught && fishSubtype) {
      return this.getWaypointHeading(fishSubtype);
    }

    if (resolvedType === 'canyon') return 'Spot';
    if (resolvedType === 'inlet') return 'Inlet';
    if (resolvedType === 'anchorage') return 'Anchorage';
    if (resolvedType === 'wreck') return 'Wreck';
    if (resolvedType === 'home') return 'Home';

    return wp.name || wp.waypoint_name || 'Waypoint';
  }

  leaveFleet() {
    if (this.requesting || !this.fleet?._id) return;
    this.showLeaveConfirm = true;
  }

  confirmLeave() {
    if (this.requesting) return;
    // Keep the confirm dialog open with a spinner until the server responds.
    this.requesting = true;
    this.socialService.leaveCommunity(this.fleet._id).subscribe({
      next: () => {
        this.requesting = false;
        this.showLeaveConfirm = false;
        this.fleet.request_status = null;
        this.toast.success('Successfully left the fleet');
        setTimeout(() => this.closePopup(), 1500);
      },
      error: (err: any) => {
        console.error('Error leaving fleet:', err);
        this.requesting = false;
        this.toast.error('Failed to leave fleet. Please try again.');
      },
    });
  }


  getDetails(type: string): string {
    return this.fishIconMap[type] || '/assets/common_icons/default_icon.svg';
  }
  requestToJoin() {
    if (this.requesting || !this.fleet?._id) return;

    this.requesting = true;
    this.socialService.sendJoinRequest(this.fleet._id).subscribe({
      next: () => {
        this.requesting = false;
        this.fleet.request_status = 'pending';
        this.toast.success('Join request sent');
      },
      error: (err: any) => {
        console.error('Error sending join request:', err);
        this.requesting = false;
        this.toast.error('Failed to send join request. Please try again.');
      },
    });
  }

  canEditPost(post: any): boolean {
    const userId = this.authService.getUserObjectId();
    const authId = this.authService.getUserIdFromToken();
    const creatorId = post.audit_data?.create_user_id
      || post.source_profile?._id
      || post.source_profile?.id
      || post.trip_details?.audit_data?.create_user_id
      || post.waypoint_details?.audit_data?.create_user_id;
    if (!creatorId) return false;
    return creatorId === userId || creatorId === authId;
  }

  getInitials(name: string): string {
    if (!name) return '';
    const parts = name.trim().split(' ');
    if (parts.length === 1) {
      return parts[0].substring(0, 2).toUpperCase();
    }
    const first = parts[0][0];
    const second = parts[1][0];
    return (first + second).toUpperCase();
  }

  private normalizeFishSubtype(value: any): string {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, '_');
  }

  private inferFishSubtypeFromText(value: any): string {
    const text = String(value || '').toLowerCase();
    if (!text) return '';
    const candidates = Object.keys(this.fishCaughtTabIconMap);
    const found = candidates.find((key) => text.includes(key.replace(/_/g, ' ')) || text.includes(key));
    return found || '';
  }

  private isFishCaughtType(type: any): boolean {
    return resolveWaypointType(type) === 'fish_caught'
      || String(type?.name || '').toLowerCase().includes('caught');
  }

  private resolveFishSubtypeForIcon(type: any): string | undefined {
    return [
      type?.details?.fish_subtype,
      type?.details?.subtype,
      type?.fish_subtype,
      type?.subtype,
      this.inferFishSubtypeFromText(type?.name),
      this.inferFishSubtypeFromText(type?.waypoint_name),
      this.inferFishSubtypeFromText(type?.title),
    ]
      .map((candidate) => this.normalizeFishSubtype(candidate))
      .find((candidate) => !!candidate);
  }

  // Round icon for the card header.
  getWaypointIcon(type: any): string {
    if (this.isFishCaughtType(type)) {
      const fishSubtype = this.resolveFishSubtypeForIcon(type);
      if (fishSubtype && this.fishCaughtTabIconMap[fishSubtype]) {
        return this.fishCaughtTabIconMap[fishSubtype];
      }
      return 'assets/waypoint_icons/caught_tab.png';
    }
    return getWaypointIconAsset(type, 'card');
  }

  // Pin-shaped marker for the static-map overlay.
  getWaypointPinIcon(type: any): string {
    if (this.isFishCaughtType(type)) {
      const fishSubtype = this.resolveFishSubtypeForIcon(type);
      if (fishSubtype && this.fishCaughtTabIconMap[fishSubtype]) {
        return this.fishCaughtTabIconMap[fishSubtype].replace('_tab.png', '_dot.png');
      }
      return 'assets/waypoint_icons/fishcaught_icon.svg';
    }
    return getWaypointIconAsset(type, 'map');
  }

  getStaticMapUrl(wp: any): string {
    const pos = this.extractPosDetails(wp?.position);
    if (!pos) return '';

    const zoom = 12;
    const width = 400;
    const height = 200;

    return `https://api.maptiler.com/maps/019adf7b-1688-7ff4-ae90-c50ef00338f9/static/${pos.lng},${pos.lat},${zoom}/${width}x${height}.png?key=IfvuVnjKOAzdw0Wrs5eg`;
  }

  formatCoordinate(val: number, type: 'lat' | 'lng'): string {
    if (val === undefined || val === null || isNaN(val)) return '--';
    const absVal = Math.abs(val);
    const degrees = Math.floor(absVal);
    const minutes = ((absVal - degrees) * 60).toFixed(1);
    const direction =
      type === 'lat' ? (val >= 0 ? 'N' : 'S') : val >= 0 ? 'E' : 'W';
    return `${degrees}° ${minutes}' ${direction}`;
  }

  getWaterTempF(tempC: number | null | undefined): string {
    if (tempC == null) return 'N/A';
    return ((tempC * 9) / 5 + 32).toFixed(1);
  }

  getWaterDepthFt(depthM: number | null | undefined): string {
    if (depthM == null) return 'N/A';
    return (depthM * 3.28084).toFixed(1);
  }

  getSogKnots(sogMs: any): string {
    if (sogMs == null || isNaN(Number(sogMs))) return 'N/A';
    return (Number(sogMs) * 1.94384).toFixed(1) + ' kts';
  }

  getFullCoordinates(pos: any): string {
    const resolved = this.extractPosDetails(pos);
    if (!resolved) return 'N/A';
    return `${this.formatCoordinate(resolved.lat, 'lat')} ${this.formatCoordinate(resolved.lng, 'lng')}`;
  }

  getWaypointImages(wp: any): string[] {
    const urls = wp.image_urls || [];
    const images = wp.images || [];

    const combined = [...urls];
    images.forEach((img: any) => {
      if (img.image_url && !combined.includes(img.image_url)) {
        combined.push(img.image_url);
      }
    });

    return combined.filter((u) => u && typeof u === 'string');
  }

  getCategoryIcon(category: string): string {
    if (!category) return '';
    return this.categoryIcons[category.toLowerCase()] || '';
  }

  getMoonIllumDisplay(value: any): string {
    if (value == null || value === '') return '--';
    const num = parseFloat(value);
    if (isNaN(num)) return String(value);
    // API usually sends fraction (0..1); if already >1 treat as whole percent
    const pct = num > 1 ? Math.round(num) : Math.round(num * 100);
    return `${pct}%`;
  }
}
